import { SupplyType } from "../../utils/booking/supplyType";
import { endPoints } from "../../constant/api_constants/url";
import { API_METHODS } from "../../enums";
import { RoomInfo } from "../../types";
import { fetchApi } from "../../utils/apiFunction";
import { consoleLog } from "../../utils/consoleLog/consoleLog";

/**
 * "Share whole cart" API layer.
 *
 * - createSharedCartAPI: store a cart snapshot (the shared-details payload) so it
 *   can be opened later via `/shared-cart/<id>`. The backend stamps the sharer's
 *   userId from the JWT — do NOT trust a body userId for that. Returns { id }.
 * - getSharedCartAPI: fetch a stored snapshot by id (recipient side).
 * - getSharedCartDetailsAPI: reconstruct fresh flight + hotel + activity details
 *   from the stored snapshot ids (proxies the booking engine
 *   `/be/v1/search/shared-details`). Used to rebuild a bookable review.
 */

export interface iSharedCartStay {
  city: string;
  country: string;
  latitude: string;
  longitude: string;
  checkIn: string;
  checkOut: string;
  hotelName?: string;
  address?: string;
  radiusMiles?: number;
}

export interface iSharedCartHotelSearch {
  stays: iSharedCartStay[];
  rooms: RoomInfo[];
}

export interface iSharedCartFlightSegment {
  departure: string;
  arrival: string;
  departureDate: string;
  returnDate?: string;
}

export interface iSharedCartFlightSearch {
  tripType: 1 | 2 | 3;
  cabin: "E" | "P" | "B" | "F";
  adults: number;
  children: number;
  infants: number;
  segments: iSharedCartFlightSegment[];
}

export interface iSharedCartSearch {
  hotel?: iSharedCartHotelSearch;
  flight?: iSharedCartFlightSearch;
}

export interface iSharedCartPayload {
  // The viewer/sharer userId sent to the shared-details engine call. On share
  // this is the sharer; on open it is the recipient (their own booking context).
  userId?: string;
  flight?: { id: string; fareId: string } | null;
  hotels?: Array<{
    uniqueId: string;
    sessionId: string;
    hotelId: string;
    unicaId: string;
    // Persisted at share time so the recipient's prebook can reuse them — the
    // /search/shared-details response does NOT return these session ids.
    preBookId?: string;
    roomPricingSessionId?: string;
    supplier?: string;
    rateHotelId?: string;
  }>;
  activity?: Array<{ uniqueId: string; productOptionCode: string; startTime: string }>;
  // Stored with the snapshot only — app-backend does NOT forward it to the
  // engine's shared-details call. Restored on open so the recipient's prebook
  // can echo the sharer's search supplyType.
  supplyTypes?: { flight?: SupplyType; hotels?: Array<SupplyType | undefined> };
  search?: iSharedCartSearch;
}

export const createSharedCartAPI = async (
  accessJWT: string,
  payLoad: { data: iSharedCartPayload; tripName?: string }
) => {
  consoleLog(["CREATE SHARED CART PAYLOAD", payLoad]);
  return await fetchApi(accessJWT, endPoints.createSharedCart, API_METHODS.POST, payLoad);
};

export const getSharedCartAPI = async (accessJWT: string, id: string) => {
  return await fetchApi(accessJWT, endPoints.getSharedCart, API_METHODS.GET, null, { id });
};

export const getSharedCartDetailsAPI = async (accessJWT: string, payLoad: iSharedCartPayload) => {
  consoleLog(["SHARED CART DETAILS PAYLOAD", payLoad]);
  return await fetchApi(accessJWT, endPoints.sharedCartDetails, API_METHODS.POST, payLoad);
};
